import { useMemo } from 'react'

import { usePrincipal } from '../../../../application/authentication'
import { isInputConfig } from '../../../../application/formBuilder/config'
import { usePolicy } from '../../../../application/product/policy/PolicyProvider'
import {
  Data,
  PolicyAdjustmentResponse,
  PolicyChangeType,
  Profile,
} from '../../../../domain'
import { useAdjustments } from '../../../../state'
import { FormConfig } from '../../../../state/configuration'
import { useApiErrorHandlingAsyncCallback } from '../../../auth'
import { useErrorHandlingAsyncCallback } from '../../../errors'
import { hasChanges } from '../helpers'
import { useAdditionalProfilePolicyAdjustmentRequest } from './adjustments'
import { toAdditionalProfilePolicyData } from './data'

export const useAdditionalProfileFormData = (
  form: FormConfig,
  profile: Profile | undefined,
) => {
  const { getAdjustment } = useAdjustments()

  return useMemo(() => {
    const toData = toAdditionalProfilePolicyData(profile)

    return form.filter(isInputConfig).reduce((data, input) => {
      const adjustment = getAdjustment(input.name)

      return {
        ...data,
        [input.name]: toData(adjustment),
      }
    }, {})
  }, [form, profile])
}

export const useSubmitRemoveProfilePolicyAdjustment = (
  onSubmitted?: (response: PolicyAdjustmentResponse) => void,
) => {
  const { policy } = usePolicy()
  const { apiClient } = usePrincipal()

  return useErrorHandlingAsyncCallback(async (profile: Profile, effectiveAt?: string) => {
    const response = await apiClient.createPolicyAdjustment({
      policyId: policy.policyId,
      adjustmentType: 'MTA',
      changes: [
        {
          changeType: PolicyChangeType.REMOVE_PROFILE,
          profileCode: profile.profileCode,
        },
      ],
      ...(effectiveAt && { effectiveAt }),
    })

    onSubmitted?.(response)
  })
}

export const useUpdateAdditionalProfilePolicyAdjustment = (
  form: FormConfig,
  profile: Profile,
  onSubmitted?: (response?: PolicyAdjustmentResponse) => void,
) => {
  const { apiClient } = usePrincipal()

  const existingData = useAdditionalProfileFormData(form, profile)
  const proposalAdjustmentRequest = useAdditionalProfilePolicyAdjustmentRequest(
    form,
    profile,
  )

  return useApiErrorHandlingAsyncCallback(async (data: Data, effectiveAt?: string) => {
    const policyAdjustment = proposalAdjustmentRequest(existingData, data, effectiveAt)

    if (hasChanges(policyAdjustment)) {
      const response = await apiClient.createPolicyAdjustment(policyAdjustment)
      onSubmitted?.(response)
    } else {
      onSubmitted?.()
    }
  })
}
